import { useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser'
import { apiRequest, graphRequest } from './msal-config'
import { logger } from '@/lib/logger'

/**
 * Error codes from BrowserAuthError that should trigger an interactive login
 * rather than being re-thrown.
 *
 * MSAL v5 uses "timed_out" for iframe-based silent auth timeouts
 * (previously "monitor_window_timeout" in v2/v3).
 */
const _INTERACTION_REQUIRED_CODES = new Set([
  'timed_out',             // MSAL v5: silent iframe blocked by browser sandbox
  'monitor_window_timeout', // MSAL v2/v3 equivalent (kept for safety)
  'token_renewal_error',   // generic renewal failure
])

/**
 * Returns true for any error that should trigger an interactive login
 * instead of being re-thrown. Covers:
 *  - InteractionRequiredAuthError (refresh token expired, consent required, etc.)
 *  - BrowserAuthError: timed_out (MSAL v5 silent iframe timeout —
 *    the "Unsafe attempt to initiate navigation" console error)
 */
function _requiresInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true
  if (error instanceof BrowserAuthError) {
    return _INTERACTION_REQUIRED_CODES.has(error.errorCode)
  }
  return false
}

/** True if MSAL already has an interaction running (redirect/popup in flight). */
function _isInteractionInProgress(error: unknown): boolean {
  return (
    error instanceof BrowserAuthError &&
    error.errorCode === 'interaction_in_progress'
  )
}

/**
 * Hook for acquiring access tokens for API calls
 * 
 * Automatically handles:
 * - Silent token acquisition (using cached refresh token)
 * - Interactive token acquisition (when refresh token expired)
 * - Token expiration (AADSTS700084 error)
 * 
 * @example
 * ```tsx
 * const { getAccessToken } = useApiToken()
 * 
 * const callApi = async () => {
 *   const token = await getAccessToken()
 *   if (!token) return
 *   
 *   const response = await fetch('/api/data', {
 *     headers: { Authorization: `Bearer ${token}` }
 *   })
 * }
 * ```
 */
export function useApiToken() {
  const { instance, accounts } = useMsal()

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (accounts.length === 0) {
      // Expected during initial render before MSAL populates accounts.
      // Callers handle null gracefully; no need to warn.
      return null
    }

    const request = {
      ...apiRequest,
      account: accounts[0],
    }

    try {
      // Try silent token acquisition first (uses cached refresh token)
      const response = await instance.acquireTokenSilent(request)
      return response.accessToken
    } catch (error) {
      if (_isInteractionInProgress(error)) {
        // Another component already triggered a redirect — return null silently.
        // The redirect will refresh the page and tokens will be available afterwards.
        return null
      }
      if (_requiresInteraction(error)) {
        // Silent acquisition failed: refresh token expired (AADSTS700084) OR
        // the hidden iframe timed out because the browser blocked its navigation
        // (timed_out / "Unsafe attempt to initiate navigation" in MSAL v5).
        logger.info('Auth', 'Silent token acquisition failed, triggering interactive login')
        try {
          await instance.acquireTokenRedirect(request)
        } catch (redirectError) {
          // Swallow interaction_in_progress if another component beat us to it
          if (!_isInteractionInProgress(redirectError)) throw redirectError
        }
        return null
      }
      logger.error('Auth', 'Token acquisition error', error)
      throw error
    }
  }, [accounts, instance])

  return { getAccessToken }
}

/**
 * Hook for Microsoft Graph API token acquisition
 * 
 * @example
 * ```tsx
 * const { getGraphToken } = useGraphToken()
 * 
 * const getUserProfile = async () => {
 *   const token = await getGraphToken()
 *   const response = await fetch('https://graph.microsoft.com/v1.0/me', {
 *     headers: { Authorization: `Bearer ${token}` }
 *   })
 * }
 * ```
 */
export function useGraphToken() {
  const { instance, accounts } = useMsal()

  const getGraphToken = useCallback(async (): Promise<string | null> => {
    if (accounts.length === 0) return null

    const request = {
      ...graphRequest,
      account: accounts[0],
    }

    try {
      const response = await instance.acquireTokenSilent(request)
      return response.accessToken
    } catch (error) {
      if (_isInteractionInProgress(error)) return null
      if (_requiresInteraction(error)) {
        await instance.acquireTokenRedirect(request)
        return null
      }
      throw error
    }
  }, [accounts, instance])

  return { getGraphToken }
}

/**
 * Hook for authentication state and user info
 * 
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout } = useAuth()
 * 
 * return (
 *   <div>
 *     {isAuthenticated ? (
 *       <>
 *         <p>Welcome {user?.name}</p>
 *         <button onClick={logout}>Logout</button>
 *       </>
 *     ) : (
 *       <button onClick={login}>Login</button>
 *     )}
 *   </div>
 * )
 * ```
 */
export function useAuth() {
  const { instance, accounts } = useMsal()
  
  const isAuthenticated = accounts.length > 0
  const user = accounts[0] || null

  const login = () => {
    instance.loginRedirect(apiRequest)
  }

  const logout = () => {
    instance.logoutRedirect({
      account: accounts[0],
    })
  }

  return {
    user,
    isAuthenticated,
    login,
    logout,
  }
}
